import React from "react";
import {
  useGetEquipmentQuery,
  useGetExercisesQuery,
  useGetMusclesQuery,
  useCreateExerciseBlockMutation,
} from "../../../../services";
import { ApiEndpoints } from "../../../../core";

interface Exercise {
  id: string;
  name: string;
  muscle: string;
  equipment: string;
  img: string;
}

interface AddExerciseModalProps {
  open: boolean;
  onClose: () => void;
  themeBox: boolean;
  addModalSelection: string | null;
  setAddModalSelection: (id: string | null) => void;
  confirmAddExercise: () => void;
  difficulty: "BEGINNER" | "ADVANCED" | "GENERAL";
  sessionFormatId: string;
  existingExerciseIds?: string[];
}

const AddExerciseModal: React.FC<AddExerciseModalProps> = ({
  open,
  onClose,
  themeBox,
  addModalSelection,
  setAddModalSelection,
  confirmAddExercise,
  difficulty,
  sessionFormatId,
  existingExerciseIds = [],
}) => {
  // Filters & State
  const [search, setSearch] = React.useState("");
  const [muscle, setMuscle] = React.useState("");
  const [equipment, setEquipment] = React.useState("");

  // Pagination & List
  const [page, setPage] = React.useState(1);
  const [exercisesList, setExercisesList] = React.useState<any[]>([]);
  const [hasMore, setHasMore] = React.useState(true);

  // Queries
  const { data: equipmentData } = useGetEquipmentQuery({ limit: -1 });
  const { data: muscleData } = useGetMusclesQuery({ limit: -1 });

  // Reset pagination when filters or search change
  React.useEffect(() => {
    setPage(1);
    setExercisesList([]);
    setHasMore(true);
  }, [search, muscle, equipment]);

  // Fetch exercises for AddExerciseModal
  const { data: exerciseData, isFetching: isFetchingExercises } = useGetExercisesQuery({
    muscle,
    equipment,
    name: search,
    page: page,
    limit: 20,
  });

  // Load next page when reaching the end
  React.useEffect(() => {
    if (exerciseData?.results) {
      if (page === 1) {
        setExercisesList(exerciseData.results);
      } else {
        setExercisesList((prev) => {
          const newResults = exerciseData.results.filter(
            (newEx: any) => !prev.some((ex) => (ex.id || (ex as any)._id) === (newEx.id || (newEx as any)._id))
          );
          return [...prev, ...newResults];
        });
      }

      if (exerciseData.results.length < 20) {
        setHasMore(false);
      }
    }
  }, [exerciseData, page]);

  // Handle scroll events
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 50 && hasMore && !isFetchingExercises) {
      setPage((prev) => prev + 1);
    }
  };

  if (!open) return null;

  // Map dynamic options from API
  const muscleList = Array.isArray(muscleData?.results) ? muscleData.results : [];
  const muscleOptions = muscleList
    .map((m: any) => ({ id: m?._id || m?.id || "", name: m?.name || "" }))
    .filter((o: any) => o.id && o.name);

  const equipmentList = Array.isArray(equipmentData?.results) ? equipmentData.results : [];
  const equipmentOptions = equipmentList
    .map((e: any) => ({ id: e?._id || e?.id || "", name: e?.name || "" }))
    .filter((o: any) => o.id && o.name);
  const list = exercisesList;

  // RTK mutation to add workout to session format
  const [createExerciseBlock, { isLoading: isAdding }] =
    useCreateExerciseBlockMutation();

  const handleAdd = async () => {
    if (!addModalSelection || !sessionFormatId) return;
    try {
      confirmAddExercise?.();
      onClose();
      setAddModalSelection(null);
    } catch (e) {
      console.error("Add exercise failed:", e);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${themeBox ? "bg-black/70" : "bg-black/40"
        }`}
    >
      <div
        className={`relative w-full max-w-2xl rounded-xl shadow-xl border ${themeBox
          ? "bg-[#1E1E1E] border-[#2C2C2C] text-gray-100"
          : "bg-white border-gray-200 text-gray-900"
          }`}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className={`absolute top-3 right-3 p-2 rounded-full ${themeBox ? "hover:bg-white/10" : "hover:bg-black/5"
            }`}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z" />
          </svg>
        </button>
        <div className="px-7 py-6">
          {/* Centered header */}
          <div className="flex flex-col items-center text-center mb-4">
            <h3 className="text-sm font-semibold mb-1">Add New Exercise</h3>
            <p
              className={`text-[11px] ${themeBox ? "text-gray-400" : "text-gray-600"
                }`}
            >
              Choose from list or search the exercise
            </p>
          </div>

          <div className="mb-4">
            <label
              className={`text-[11px] font-medium ${themeBox ? "text-gray-300" : "text-gray-700"
                }`}
            >
              Search Exercise Here
            </label>
            <div
              className={`flex items-center mt-1 rounded-[10px] ${themeBox
                ? "bg-[#1F1F1F] border border-[#9B9DB6]"
                : "bg-white border border-gray-300"
                } px-2`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
                  stroke="#949494"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M21 21L17 17"
                  stroke="#949494"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className={`flex-1 px-2 py-1.5 text-xs outline-none ${themeBox
                  ? "bg-transparent text-gray-200"
                  : "bg-transparent text-gray-800"
                  }`}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div>
              <label
                className={`text-[11px] font-medium ${themeBox ? "text-gray-300" : "text-gray-700"
                  }`}
              >
                Muscle
              </label>
              <select
                value={muscle}
                onChange={(e) => setMuscle(e.target.value)}
                className={`mt-1 w-full rounded-[10px] px-2 py-1.5 text-xs outline-none ${themeBox
                  ? "bg-[#1F1F1F] border border-[#9B9DB6] text-gray-200"
                  : "bg-white border border-gray-300 text-gray-800"
                  }`}
              >
                <option value="">All</option>
                {muscleOptions.map((m: any) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                className={`text-[11px] font-medium ${themeBox ? "text-gray-300" : "text-gray-700"
                  }`}
              >
                Equipment
              </label>
              <select
                value={equipment}
                onChange={(e) => setEquipment(e.target.value)}
                className={`mt-1 w-full rounded-[10px] px-2 py-1.5 text-xs outline-none ${themeBox
                  ? "bg-[#1F1F1F] border border-[#9B9DB6] text-gray-200"
                  : "bg-white border border-gray-300 text-gray-800"
                  }`}
              >
                <option value="">All</option>
                {equipmentOptions.map((eq: any) => (
                  <option key={eq.id} value={eq.id}>
                    {eq.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <label
            className={`text-[11px] font-medium mb-2 block ${themeBox ? "text-gray-300" : "text-gray-700"
              }`}
          >
            Choose Exercises
          </label>
          <div
            onScroll={handleScroll}
            className={`rounded-lg border ${themeBox
              ? "border-[#2C2C2C] bg-[#0D0D0D]"
              : "border-gray-300 bg-white"
              } max-h-56 overflow-y-auto custom-scroll ${themeBox ? "custom-scroll-dark" : ""} p-3`}
          >
            {list.map((ex: any) => {
              const exId = ex?.id || ex?._id;
              const alreadyAdded = existingExerciseIds.includes(String(exId));
              const active = addModalSelection === exId;
              // Build thumbnail URL if key exists
              const thumbKey = ex?.thumbnail?.key;
              let thumbSrc = ex?.img || ex?.image || "";
              if (thumbKey) {
                if (ex?.type === "DEFAULT") {
                  thumbSrc = `${ApiEndpoints.defaultWorkoutThumbnailURL}/${thumbKey}`;
                } else {
                  thumbSrc = `${ApiEndpoints.customWorkoutThumbnailURL}/${thumbKey}`;
                }
              }
              return (
                <button
                  key={exId}
                  type="button"
                  onClick={() => !alreadyAdded && setAddModalSelection(exId)}
                  disabled={alreadyAdded}
                  className={`flex items-center gap-3 w-full text-left px-3 py-2 text-xs mb-2 rounded-[10px] border transition-all ${themeBox
                    ? `border-[#9B9DB6] bg-[#1F1F1F] hover:bg-[#252525] ${active ? "bg-[#262B10] border-[#B6CB24]" : ""}`
                    : `border-gray-100 bg-white hover:bg-gray-50 ${active ? "bg-[#EEF5D2] border-[#lime-500]" : ""}`
                    } ${alreadyAdded ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <div className="w-10 h-10 rounded overflow-hidden bg-black/30 flex-shrink-0">
                    <img
                      src={thumbSrc}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span
                    className={`flex-1 font-medium ${themeBox ? "text-gray-200" : "text-gray-700"
                      }`}
                  >
                    {ex?.name || "Exercise"}
                    {alreadyAdded && (
                      <span className="ml-2 text-[10px] text-[#B6CB24] font-bold">
                        (Already Added)
                      </span>
                    )}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] ${themeBox
                      ? "bg-[#1E1E1E] text-gray-400"
                      : "bg-gray-100 text-gray-600"
                      }`}
                  >
                    {ex?.muscle?.name || ex?.muscle || ""}
                  </span>
                </button>
              );
            })}

            {list.length === 0 && !isFetchingExercises && (
              <div
                className={`px-3 py-6 text-center text-[11px] ${themeBox ? "text-gray-500" : "text-gray-500"
                  }`}
              >
                No matches
              </div>
            )}
            {isFetchingExercises && (
              <div className="flex justify-center p-4">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#B6CB24]"></div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              type="button"
              onClick={handleAdd}
              disabled={!addModalSelection || isAdding}
              className="px-8 py-2 rounded-md text-xs font-semibold text-black disabled:opacity-40"
              style={{
                background:
                  "linear-gradient(92deg, rgba(237,245,192,0.90) 46.67%, rgba(198,217,44,0.90) 116.75%)",
                boxShadow: "0 0 0 1px #C6D92C inset",
              }}
            >
              {isAdding ? "Adding..." : "Add"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className={`px-8 py-2 rounded-md text-xs font-medium ${themeBox
                ? "bg-[#2A2A2A] text-gray-300 hover:bg-[#333]"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddExerciseModal;
